import { Dot, DotKey } from './geometry'

export type AnchorLayer = { name?: string; cover?: string }
type Kind = 'pupil' | 'white' | 'socket' | 'eye' | 'mouth'
type Candidate = { kind: Kind; points: Dot[] }

const covers: Record<Kind, string[]> = {
  pupil: ['MxE', '3Jc', 'qBB'],
  white: ['miN', '0pv', 'cDZ'],
  socket: ['Vvg', 'fzg', '1LZ'],
  eye: [],
  mouth: ['0mJ', 'abW', 'uu0'],
}

export function anchorKind(layer?: AnchorLayer): Kind | undefined {
  if (!layer) return undefined
  for (const kind of Object.keys(covers) as Kind[]) {
    if (layer.cover && covers[kind].includes(layer.cover)) return kind
  }
  const name = (layer.name || '').toLowerCase()
  if (/瞳孔|pupil|iris/.test(name)) return 'pupil'
  if (/眼白|eye\s*white|sclera/.test(name)) return 'white'
  if (/眼眶|眼框|eye\s*socket/.test(name)) return 'socket'
  if (/嘴|口型|mouth/.test(name)) return 'mouth'
  if (!/眉|高光|eyebrow|highlight/.test(name) && /眼睛|双眼|eye/.test(name))
    return 'eye'
  return undefined
}

/** Find substantial disconnected shapes; use their bounds centers, including transparent interiors. */
export function componentPoints(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  splitEyes = true,
): Dot[] {
  const visited = new Uint8Array(width * height)
  const shapes: {
    count: number
    l: number
    r: number
    t: number
    b: number
  }[] = []
  const queue = new Int32Array(width * height)
  for (let i = 0; i < width * height; i++) {
    if (visited[i] || data[i * 4 + 3] <= 16) continue
    let head = 0,
      tail = 0
    queue[tail++] = i
    visited[i] = 1
    let l = width,
      r = 0,
      t = height,
      b = 0
    while (head < tail) {
      const p = queue[head++]
      const x = p % width,
        y = Math.floor(p / width)
      l = Math.min(l, x)
      r = Math.max(r, x)
      t = Math.min(t, y)
      b = Math.max(b, y)
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx,
            ny = y + dy
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue
          const next = ny * width + nx
          if (!visited[next] && data[next * 4 + 3] > 16) {
            visited[next] = 1
            queue[tail++] = next
          }
        }
    }
    shapes.push({ count: tail, l, r, t, b })
  }
  if (!shapes.length) return []
  shapes.sort((a, b) => b.count - a.count)
  const first = shapes[0],
    second = shapes[1]
  // Split only two comparably substantial, horizontally separated components.
  if (
    splitEyes &&
    second &&
    second.count >= first.count * 0.25 &&
    (first.r < second.l || second.r < first.l)
  ) {
    return [first, second].map((s) => ({
      x: (s.l + s.r + 1) / 2,
      y: (s.t + s.b + 1) / 2,
    }))
  }
  // A single feature can contain disconnected decorative details; include its full bounds.
  const substantial = shapes.filter((s) => s.count >= first.count * 0.05)
  return [
    {
      x:
        (Math.min(...substantial.map((s) => s.l)) +
          Math.max(...substantial.map((s) => s.r)) +
          1) /
        2,
      y:
        (Math.min(...substantial.map((s) => s.t)) +
          Math.max(...substantial.map((s) => s.b)) +
          1) /
        2,
    },
  ]
}

export function resolveComponentAnchors(
  candidates: Candidate[],
): Partial<Record<DotKey, Dot>> {
  const result: Partial<Record<DotKey, Dot>> = {}
  const mouths = candidates
    .filter((c) => c.kind === 'mouth')
    .flatMap((c) => c.points)
  if (mouths.length === 1) result.M = mouths[0]
  // Only a complete pair establishes spatial left and right. A single eye
  // cannot be classified by the canvas midpoint when the face is offset.
  for (const kind of ['pupil', 'white', 'socket', 'eye'] as Kind[]) {
    const points = candidates
      .filter((c) => c.kind === kind)
      .flatMap((c) => c.points)
      .sort((a, b) => a.x - b.x)
    if (points.length === 2) {
      result.L = points[0]
      result.R = points[1]
      break
    }
  }
  return result
}
